import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Shield, Smartphone, Key, Copy, CheckCircle2, AlertTriangle, RefreshCw, Eye, EyeOff, Lock, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// 2FA Setup Modal Component
export const TwoFactorSetupModal = ({ isOpen, onClose, onSuccess, mandatory = false }) => {
  const { user, setUser } = useAuth();
  const [step, setStep] = useState(1); // 1: QR Code, 2: Verify, 3: Backup Codes
  const [loading, setLoading] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [showManualKey, setShowManualKey] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState(false);

  // Fetch QR code on open
  useEffect(() => {
    if (isOpen && step === 1) {
      fetchSetupData();
    }
  }, [isOpen]);

  const fetchSetupData = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/auth/2fa/setup`);
      setSetupData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initialize 2FA setup');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/auth/2fa/verify-setup`, {
        code: verificationCode
      });
      
      setBackupCodes(response.data.backup_codes);
      
      // Backend invalidates all pre-rotation tokens via `tokens_valid_after`
      // and issues a fresh one. Swap it in so the user's current session
      // keeps working.
      const newToken = response.data.access_token;
      if (newToken) {
        localStorage.setItem('token', newToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      }

      // Refresh the AuthContext user snapshot so the "Set up 2FA" banner
      // disappears immediately. Without this the banner sticks until the
      // next hard reload because our cached user still reads
      // `totp_enabled: false`.
      if (user) {
        setUser({ ...user, totp_enabled: true });
      }
      
      setStep(3);
      toast.success('2FA enabled successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const copyManualKey = () => {
    navigator.clipboard.writeText(setupData?.secret || '');
    setCopiedKey(true);
    toast.success('Key copied to clipboard');
    setTimeout(() => setCopiedKey(false), 2000);
  };

  const copyBackupCodes = () => {
    const codesText = backupCodes.join('\n');
    navigator.clipboard.writeText(codesText);
    setCopiedCodes(true);
    toast.success('Backup codes copied');
    setTimeout(() => setCopiedCodes(false), 2000);
  };

  const handleClose = () => {
    if (step === 3) {
      onSuccess?.();
      // Don't call onClose after onSuccess - onSuccess handles the completion
      // Reset state for next time
      setStep(1);
      setSetupData(null);
      setVerificationCode('');
      setBackupCodes([]);
      return;  // Exit here - don't call onClose
    }
    // For non-step-3 closes (cancel), reset and call onClose
    setStep(1);
    setSetupData(null);
    setVerificationCode('');
    setBackupCodes([]);
    onClose();
  };

  // For mandatory mode, only allow close after completing setup
  const handleDialogChange = (open) => {
    if (!open && mandatory && step !== 3) {
      // Don't allow closing mandatory setup before completion
      return;
    }
    if (!open) {
      handleClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogChange}>
      <DialogContent className="sm:max-w-lg" hideCloseButton={mandatory && step !== 3}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#0A7EA4]" />
            {step === 1 && (mandatory ? 'Two-Factor Authentication Required' : 'Set Up Two-Factor Authentication')}
            {step === 2 && 'Verify Your Authenticator'}
            {step === 3 && 'Save Your Backup Codes'}
          </DialogTitle>
          <DialogDescription>
            {step === 1 && (mandatory 
              ? '2FA is required for your account. Scan the QR code with your authenticator app to continue.'
              : 'Scan the QR code with your authenticator app')}
            {step === 2 && 'Enter the 6-digit code from your authenticator app'}
            {step === 3 && "Store these codes securely - you'll need them if you lose your phone"}
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: QR Code */}
        {step === 1 && (
          <div className="space-y-4">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A7EA4]"></div>
              </div>
            ) : setupData && (
              <>
                <div className="flex justify-center">
                  <div className="p-4 bg-white rounded-lg border-2 border-gray-200">
                    <img 
                      src={`data:image/png;base64,${setupData.qr_code}`} 
                      alt="2FA QR Code" 
                      className="w-48 h-48"
                    />
                  </div>
                </div>

                <div className="text-center text-sm text-gray-600">
                  <p>Scan this QR code with:</p>
                  <p className="font-medium">Google Authenticator, Authy, or Microsoft Authenticator</p>
                </div>

                <div className="border-t pt-4">
                  <button 
                    onClick={() => setShowManualKey(!showManualKey)}
                    className="text-sm text-[#0A7EA4] hover:underline flex items-center gap-1"
                  >
                    {showManualKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    {showManualKey ? 'Hide' : 'Show'} manual entry key
                  </button>
                  
                  {showManualKey && (
                    <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between gap-2">
                        <code className="text-sm font-mono">{setupData.manual_entry_key}</code>
                        <Button size="sm" variant="ghost" onClick={copyManualKey}>
                          {copiedKey ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Step 2: Verify Code */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="flex justify-center">
              <Smartphone className="w-16 h-16 text-[#0A7EA4]" />
            </div>
            
            <div>
              <Label>Enter 6-digit code</Label>
              <Input
                type="text"
                maxLength={6}
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => e.key === 'Enter' && verificationCode.length === 6 && !loading && handleVerify()}
                className="text-center text-2xl tracking-widest mt-2"
                autoFocus
              />
            </div>

            <p className="text-sm text-gray-500 text-center">
              Open your authenticator app and enter the code shown for MARQ
            </p>
          </div>
        )}

        {/* Step 3: Backup Codes */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-semibold">Important!</p>
                  <p>Save these backup codes now. They will not be shown again.</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 p-4 bg-gray-50 rounded-lg">
              {backupCodes.map((code, index) => (
                <code key={index} className="text-sm font-mono p-2 bg-white rounded border text-center">
                  {code}
                </code>
              ))}
            </div>

            <div className="flex justify-center">
              <Button variant="outline" onClick={copyBackupCodes}>
                {copiedCodes ? (
                  <><CheckCircle2 className="w-4 h-4 mr-2 text-green-600" />Copied!</>
                ) : (
                  <><Copy className="w-4 h-4 mr-2" />Copy All Codes</>
                )}
              </Button>
            </div>

            <p className="text-sm text-gray-500 text-center">
              Each backup code can only be used once. Store them in a safe place.
            </p>
          </div>
        )}

        <DialogFooter>
          {step === 1 && (
            <>
              {!mandatory && <Button variant="outline" onClick={handleClose}>Cancel</Button>}
              <Button onClick={() => setStep(2)} disabled={!setupData} className={mandatory ? 'w-full' : ''}>
                Next: Verify Code
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button onClick={handleVerify} disabled={loading || verificationCode.length !== 6}>
                {loading ? 'Verifying...' : 'Verify & Enable'}
              </Button>
            </>
          )}
          {step === 3 && (
            <Button onClick={handleClose} className="w-full">
              <CheckCircle2 className="w-4 h-4 mr-2" />I have Saved My Backup Codes
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// 2FA Verification Modal (for login)
export const TwoFactorVerifyModal = ({ isOpen, onClose, onSuccess, onRecover }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [showRecoveryOption, setShowRecoveryOption] = useState(false);

  const handleVerify = async () => {
    if (!code || code.length < 6) {
      toast.error('Please enter a valid code');
      return;
    }

    try {
      setLoading(true);
      const screenInfo = `${window.screen.width}x${window.screen.height}`;
      const response = await axios.post(`${API}/auth/2fa/verify`, {
        code,
        screen_info: screenInfo,
      });
      
      if (response.data.backup_codes_remaining !== undefined && response.data.backup_codes_remaining < 5) {
        toast.warning(`Only ${response.data.backup_codes_remaining} backup codes remaining`);
      }
      
      // Swap the restricted pre-auth token for the fresh full-access token
      // that the backend just issued.
      const newToken = response.data.access_token;
      const newSessionId = response.data.session_id;
      if (newToken) {
        localStorage.setItem('token', newToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      }
      // Persist session_id so profile + admin session management can flag
      // "this is your current session".
      if (newSessionId) {
        localStorage.setItem('session_id', newSessionId);
      }
      
      toast.success('Verification successful');
      onSuccess?.(newToken);
      // Don't call onClose here - let onSuccess handle the cleanup
      // onClose is only for cancellation
    } catch (error) {
      // Backend may return either a plain string detail (e.g. "Invalid
      // verification code") OR a structured dict when a non-TOTP reason
      // blocked the request (e.g. session_limit_exceeded, expired pre-auth
      // token). Render both correctly instead of showing `[object Object]`.
      const detail = error.response?.data?.detail;
      let msg = 'Invalid code';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (detail && typeof detail === 'object') {
        if (detail.session_limit_exceeded) {
          msg = detail.message
            ? `${detail.message} (${detail.active_sessions}/${detail.max_sessions} devices)`
            : `Device limit reached (${detail.active_sessions}/${detail.max_sessions}). Log out from another device first.`;
        } else {
          msg = detail.message || 'Sign-in blocked. Please try again.';
        }
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleVerify();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md" hideCloseButton>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#0A7EA4]" />
            Two-Factor Authentication
          </DialogTitle>
          <DialogDescription>
            Enter the 6-digit code from your authenticator app or a backup code
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Verification Code</Label>
            <Input
              type="text"
              maxLength={8}
              placeholder="Enter code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase())}
              onKeyDown={handleKeyDown}
              className="text-center text-xl tracking-widest mt-2"
              autoFocus
            />
            <p className="text-xs text-gray-500 mt-1 text-center">
              6-digit code or 8-character backup code
            </p>
          </div>

          <div className="text-center">
            <button
              onClick={() => setShowRecoveryOption(!showRecoveryOption)}
              className="text-sm text-[#0A7EA4] hover:underline"
            >
              Lost access to your authenticator?
            </button>
            
            {showRecoveryOption && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm">
                <p className="text-gray-600 mb-2">You can recover access using:</p>
                <ul className="text-left text-gray-600 space-y-1 mb-3">
                  <li>• Your backup codes (enter above)</li>
                  <li>• Security questions recovery</li>
                </ul>
                <Button variant="outline" size="sm" onClick={onRecover} className="w-full">
                  <Key className="w-4 h-4 mr-2" />
                  Recover via Security Questions
                </Button>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel Login</Button>
          <Button onClick={handleVerify} disabled={loading || code.length < 6}>
            {loading ? 'Verifying...' : 'Verify'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// 2FA Status Card Component (for Profile page)
export const TwoFactorStatusCard = ({ onSetupClick }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateCode, setRegenerateCode] = useState('');
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [newBackupCodes, setNewBackupCodes] = useState([]);
  // Reset 2FA state
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/2fa/status`);
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch 2FA status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Handle Reset 2FA with password verification
  const handleReset = async () => {
    if (!resetPassword) {
      toast.error('Please enter your password');
      return;
    }

    try {
      setResetting(true);
      // First disable current 2FA
      await axios.post(`${API}/auth/2fa/disable`, { password: resetPassword });
      toast.success('2FA disabled. Setting up new 2FA...');
      setShowResetDialog(false);
      setResetPassword('');
      // Now trigger the setup flow
      onSetupClick();
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Incorrect password');
    } finally {
      setResetting(false);
    }
  };

  const handleRegenerate = async () => {
    if (!regenerateCode || regenerateCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    try {
      setRegenerating(true);
      const response = await axios.post(`${API}/auth/2fa/regenerate-backup-codes`, { code: regenerateCode });
      setNewBackupCodes(response.data.backup_codes);
      toast.success('Backup codes regenerated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to regenerate codes');
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A7EA4]"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Don't show if 2FA is globally disabled for this user's role
  if (!status?.can_setup) {
    return null;
  }

  const isMandatory = status?.is_mandatory;
  const moduleStatus = status?.module_status; // 'active', 'coming_soon', or 'disabled'

  return (
    <>
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className={`p-3 rounded-full ${status?.totp_enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                {status?.totp_enabled ? (
                  <Lock className="w-6 h-6 text-green-600" />
                ) : (
                  <Unlock className="w-6 h-6 text-gray-500" />
                )}
              </div>
              <div>
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  Two-Factor Authentication
                  {isMandatory && !status?.totp_enabled && (
                    <Badge className="bg-red-100 text-red-700 text-xs">Required</Badge>
                  )}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={status?.totp_enabled ? 'bg-green-600' : 'bg-gray-500'}>
                    {status?.totp_enabled ? 'Enabled' : 'Not Set Up'}
                  </Badge>
                  {status?.totp_enabled && status?.backup_codes_remaining < 5 && (
                    <Badge variant="outline" className="text-amber-600 border-amber-300">
                      {status.backup_codes_remaining} backup codes left
                    </Badge>
                  )}
                </div>
                {status?.totp_enabled && (
                  <p className="text-sm text-gray-500 mt-2">
                    Enabled on {new Date(status.totp_setup_at).toLocaleDateString()}
                  </p>
                )}
                {!status?.totp_enabled && isMandatory && (
                  <p className="text-sm text-red-600 mt-2">
                    Two-factor authentication is required. Please set it up to continue using the app securely.
                  </p>
                )}
                {!status?.totp_enabled && !isMandatory && moduleStatus === 'coming_soon' && (
                  <p className="text-sm text-gray-500 mt-2">
                    Add an extra layer of security to your account with two-factor authentication.
                  </p>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              {status?.totp_enabled ? (
                <>
                  <Button variant="outline" size="sm" onClick={() => setShowRegenerateDialog(true)}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    New Backup Codes
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setShowResetDialog(true)}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reset 2FA
                  </Button>
                </>
              ) : (
                <Button onClick={onSetupClick} className={isMandatory ? 'bg-red-600 hover:bg-red-700' : ''}>
                  <Shield className="w-4 h-4 mr-2" />
                  {isMandatory ? 'Set Up 2FA (Required)' : 'Enable 2FA'}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Regenerate Backup Codes Dialog */}
      <Dialog open={showRegenerateDialog} onOpenChange={(open) => {
        if (!open) {
          setShowRegenerateDialog(false);
          setRegenerateCode('');
          setNewBackupCodes([]);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-[#0A7EA4]" />
              Regenerate Backup Codes
            </DialogTitle>
            <DialogDescription>
              {newBackupCodes.length > 0 
                ? 'Save these new backup codes. Previous codes are now invalid.'
                : 'Enter a code from your authenticator to generate new backup codes.'}
            </DialogDescription>
          </DialogHeader>
          
          {newBackupCodes.length === 0 ? (
            <div>
              <Label>Authenticator Code</Label>
              <Input
                type="text"
                maxLength={6}
                placeholder="000000"
                value={regenerateCode}
                onChange={(e) => setRegenerateCode(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => e.key === 'Enter' && regenerateCode.length === 6 && !regenerating && handleRegenerate()}
                className="text-center text-xl tracking-widest mt-2"
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 p-4 bg-gray-50 rounded-lg">
              {newBackupCodes.map((code, index) => (
                <code key={index} className="text-sm font-mono p-2 bg-white rounded border text-center">
                  {code}
                </code>
              ))}
            </div>
          )}

          <DialogFooter>
            {newBackupCodes.length === 0 ? (
              <>
                <Button variant="outline" onClick={() => setShowRegenerateDialog(false)}>Cancel</Button>
                <Button onClick={handleRegenerate} disabled={regenerating || regenerateCode.length !== 6}>
                  {regenerating ? 'Generating...' : 'Generate New Codes'}
                </Button>
              </>
            ) : (
              <Button onClick={() => setShowRegenerateDialog(false)} className="w-full">
                <CheckCircle2 className="w-4 h-4 mr-2" />Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset 2FA Dialog (requires password) */}
      <Dialog open={showResetDialog} onOpenChange={(open) => {
        if (!open) {
          setShowResetDialog(false);
          setResetPassword('');
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-[#0A7EA4]" />
              Reset Two-Factor Authentication
            </DialogTitle>
            <DialogDescription>
              This will disable your current 2FA and generate a new QR code. You will need to re-register in your authenticator app.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <strong>Warning:</strong> Your existing backup codes will be invalidated.
              </p>
            </div>
            
            <div>
              <Label>Enter your password to confirm</Label>
              <Input
                type="password"
                placeholder="Your password"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && resetPassword && !resetting && handleReset()}
                className="mt-2"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetDialog(false)}>Cancel</Button>
            <Button onClick={handleReset} disabled={resetting || !resetPassword}>
              {resetting ? 'Resetting...' : 'Reset & Setup New 2FA'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default { TwoFactorSetupModal, TwoFactorVerifyModal, TwoFactorStatusCard };
